/**
 * Top level component
 *
 * Copyright (C) 2020A Noom, Inc.
 * @author nikola
 */

import React, { Suspense, useEffect } from "react";
import { BrowserRouter, Route } from "react-router-dom";
// Datadog's drop-in replacement for `Routes`. Behaves identically, but names
// each RUM view after the matched route pattern (`/care/treatment/:treatmentCode`)
// instead of the raw URL. Inert when RUM is not initialized.
import { Routes } from "@datadog/browser-rum-react/react-router-v6";
import { ApolloProvider } from "@noom/hive-core";
import { Logout, PublicRoute, SecureRoute } from "@noom/hive-auth";
import { useLogger } from "@noom/hive-analytics";
import { ThemeProvider } from "@noom/wax-component-library";

import { ErrorBoundary } from "@modules/common";
import { init as initI18n } from "@modules/localization";
import { apolloClient } from "@modules/dataFetching/apolloClient";
import { MainLayout } from "@modules/layout";
import { HomePage } from "@pages/HomePage";
import { LoginPage } from "@pages/LoginPage";
import { VisitsPage } from "@pages/VisitsPage";
import { DocumentsPage } from "@pages/DocumentsPage";
import { ShippingAndRefillsPage } from "@pages/ShippingAndRefillsPage";
import { UpdateRefillPage } from "@pages/UpdateRefillPage";
import { RequirePatient } from "@modules/authentication/RequirePatient";
import { clearLocalSessionState } from "@modules/authentication/session";
import { PrescriptionsPage } from "@pages/PrescriptionsPage";
import { NativeDemoPage } from "@pages/NativeDemoPage";
import { RedirectPage } from "@pages/RedirectPage";
import { ConfirmDeliveryPage } from "@pages/ConfirmDeliveryPage";
import { ConfirmRefillPage } from "@pages/ConfirmRefillPage";
import { ConfirmPaymentPage } from "@pages/ConfirmPaymentPage";
import { PharmacySwitchPage } from "@pages/PharmacySwitchPage";
import { ConcentrationChangePage } from "@pages/ConcentrationChangePage";
import { ConcentrationChangeIncreasePage } from "@pages/ConcentrationChangeIncreasePage";
import { CompoundFormulationChangePage } from "@pages/CompoundFormulationChangePage";
import { PrescriptionFulfillmentDetailPage } from "@pages/PrescriptionFulfillmentDetailPage";
import { InsuranceFulfillmentDetailPage } from "@pages/InsuranceFulfillmentDetailPage";
import { CareTabPage } from "@modules/care/CareTabPage";
import { TreatmentDetailPage } from "@modules/care/TreatmentDetailPage";
import { NotFoundPage } from "@pages/NotFoundPage";
import { BrandNeutralLoading } from "common/NoomLoader";

import { trackRumLogout } from "./rumMonitoring";
import { useAuthTelemetry } from "./useAuthTelemetry";
import theme from "./theme";

void initI18n();

// Logout route: clear the cross-app SSO cookie, the native auto-login cookie,
// and session-scoped state, then let hive-auth tear down the auth session and
// redirect to /login. Clearing `sessionStorage` prevents analytics dedup flags
// from bleeding across users on the same tab / WebView.
const AppLogout = () => {
  const mixpanelLogger = useLogger();

  useEffect(() => {
    // Only reachable from the NavBar button, which every WebView-embedded
    // route hides via `showHeader: false`. Reported anyway so a WebView
    // session loss can be positively ruled out rather than assumed.
    trackRumLogout("user_initiated");
    clearLocalSessionState(mixpanelLogger);
  }, [mixpanelLogger]);

  return <Logout successPath="/login" />;
};

// Return route components for the application
export const getRoutes = () => {
  const routes = [
    {
      path: "/",
      component: HomePage,
      requiresPatient: false,
      // Redirect-only page; hide Med NavBar while profile resolves.
      showHeader: false,
      showFooter: false,
    },
    { path: "/visits/:filterType", component: VisitsPage },
    { path: "/prescriptions", component: PrescriptionsPage },
    { path: "/documents", component: DocumentsPage },
    {
      path: "/shipping-and-refills",
      component: ShippingAndRefillsPage,
      showHeader: false,
      showFooter: false,
      requiresPatient: false,
    },
    {
      path: "/update-refill",
      component: UpdateRefillPage,
      showHeader: false,
      showFooter: false,
    },
    {
      path: "/confirm-delivery",
      component: ConfirmDeliveryPage,
      showHeader: false,
      showFooter: false,
    },
    {
      path: "/confirm-refill",
      component: ConfirmRefillPage,
      showHeader: false,
      showFooter: false,
    },
    {
      path: "/confirm-payment",
      component: ConfirmPaymentPage,
      showHeader: false,
      showFooter: false,
    },
    { path: "/login", component: LoginPage, secured: false },
    {
      path: "/native-demo",
      component: NativeDemoPage,
      showHeader: false,
      showFooter: false,
    },
    {
      path: "/redirect",
      component: RedirectPage,
      showHeader: false,
      showFooter: false,
    },
    {
      path: "/pharmacy-switch",
      component: PharmacySwitchPage,
      showHeader: false,
      showFooter: false,
    },
    {
      path: "/concentration-change-decrease",
      component: ConcentrationChangePage,
      showHeader: false,
      showFooter: false,
    },
    {
      path: "/prescription-fulfillment-detail",
      component: PrescriptionFulfillmentDetailPage,
      showHeader: false,
      showFooter: false,
    },
    {
      path: "/insurance-fulfillment-detail",
      component: InsuranceFulfillmentDetailPage,
      showHeader: false,
      showFooter: false,
    },
    {
      path: "/care",
      component: CareTabPage,
      showHeader: false,
      showFooter: false,
      requiresPatient: false,
    },
    {
      path: "/care/treatment/:treatmentCode",
      component: TreatmentDetailPage,
      showHeader: false,
      showFooter: false,
      requiresPatient: false,
    },
    {
      path: "/concentration-change-increase",
      component: ConcentrationChangeIncreasePage,
      showHeader: false,
      showFooter: false,
    },
    {
      path: "/compound-formulation-change",
      component: CompoundFormulationChangePage,
      showHeader: false,
      showFooter: false,
    },
  ];

  return (
    <Routes>
      <Route path="/logout" element={<AppLogout />} />
      {routes.map((route) => {
        const layout = (
          <MainLayout
            showHeader={route.showHeader}
            showFooter={route.showFooter}
          >
            <route.component />
          </MainLayout>
        );

        if (route.secured === false) {
          return (
            <Route
              key={route.path}
              path={route.path}
              element={<PublicRoute>{layout}</PublicRoute>}
            />
          );
        }

        // Secured routes additionally enforce the telehealth-patient gate via
        // `RequirePatient`, unless they opt out with `requiresPatient: false`
        // (e.g. `/care`, `/shipping-and-refills` — open to all authenticated users).
        const securedContent =
          route.requiresPatient === false ? (
            layout
          ) : (
            <RequirePatient>{layout}</RequirePatient>
          );

        return (
          <Route
            key={route.path}
            path={route.path}
            element={<SecureRoute>{securedContent}</SecureRoute>}
          />
        );
      })}
      <Route
        path="*"
        element={
          <MainLayout showHeader={false} showFooter={false}>
            <NotFoundPage />
          </MainLayout>
        }
      />
    </Routes>
  );
};

const App: React.FC = () => {
  useAuthTelemetry();

  return (
    <ThemeProvider includeFonts resetCSS theme={theme}>
      <ErrorBoundary>
        <ApolloProvider client={apolloClient}>
          <BrowserRouter>
            <Suspense fallback={<BrandNeutralLoading />}>{getRoutes()}</Suspense>
          </BrowserRouter>
        </ApolloProvider>
      </ErrorBoundary>
    </ThemeProvider>
  );
};

export default App;
